import {
  CheckCircle2,
  CircleCheckBig,
  Clock,
  FileClock,
  FilePen,
  FileText,
  Info,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type {
  GetCurrentUser,
  GetSecondOpinionById,
} from '@virtual-care/client';
import {
  displayName,
  formatDate,
  formatSecondOpinionDueDate,
  getLocalizedSpeciality,
  toAge,
} from '@virtual-care/isomorphic';
import { AttachmentsDialog } from '@virtual-care/media-web';
import { Button } from '@virtual-care/shadcn';
import {
  AdditionalInfo,
  AppointmentHeader,
  AppointmentTimeline,
  Loading,
  PatientInfoCard,
  PaymentDetailsCard,
  useData,
} from '@virtual-care/ui';

import { useLanguage } from '../../../i18n/use-language.ts';
import { Header, ProfileCard } from '../../components/header/index.ts';
import { usePaymentInfo } from '../../hooks/use-payment-info.ts';
import { SubmitInfoButton } from './actions/submit-info-action.tsx';
import { SecondOpinionCard } from './second-opinion-card.tsx';
import type { StatusMap, TimelineMap } from './status.ts';

function useStatusMap({
  request,
}: {
  request?: GetSecondOpinionById;
}): StatusMap {
  const { t } = useTranslation();
  if (!request) {
    return {} as StatusMap;
  }
  const dueDate = formatSecondOpinionDueDate(request);
  const urgency = request.urgent
    ? t('secondOpinion.urgentRequest')
    : t('secondOpinion.normalRequest');

  return {
    WAITING_DOCTOR_ASSIGNMENT: {
      text: t('secondOpinion.statuses.waitingDoctorAssignment', { urgency }),
      icon: Info,
      className: 'border-orange-300 bg-orange-50 text-orange-700',
    },
    WAITING_DOCTOR_CONFIRMATION: {
      text: t('secondOpinion.statuses.waitingDoctorConfirmation', { urgency }),
      icon: Clock,
      className: 'border-blue-300 bg-blue-50 text-blue-700',
    },
    CONFIRMED: {
      text: t('secondOpinion.statuses.confirmed', { date: dueDate }),
      icon: CheckCircle2,
      className: 'border-green-300 bg-green-50 text-green-700',
    },
    FINISHED: {
      text: t('secondOpinion.statuses.finished'),
      icon: FileText,
      className: 'border-border bg-muted text-muted-foreground',
    },
  } satisfies StatusMap;
}

function useHistoryEventDetails({
  request,
}: {
  request?: GetSecondOpinionById;
}): TimelineMap {
  const { t } = useTranslation();
  if (!request) {
    return {} as TimelineMap;
  }

  const doctorName =
    `${request.doctor?.firstName || ''} ${request.doctor?.lastName || ''}`.trim();

  return {
    REFERRED_TO_ANOTHER_DOCTOR: {
      icon: Info,
      color: 'text-blue-500',
      title: t('secondOpinion.timeline.referred.title'),
      message: t('secondOpinion.timeline.referred.message'),
    },
    CREATED: {
      icon: FileClock,
      color: 'text-blue-500',
      title: t('secondOpinion.timeline.created.title', {
        number: request.counter,
      }),
      message: t('secondOpinion.timeline.created.message'),
    },
    FINISHED: {
      icon: FileText,
      color: 'text-blue-500',
      title: t('secondOpinion.timeline.finished.title'),
      message: t('secondOpinion.timeline.finished.message'),
    },
    DOCTOR_ASSIGNED: {
      icon: CircleCheckBig,
      color: 'text-green-500',
      title: t('secondOpinion.timeline.doctorAssigned.title'),
      message: t('secondOpinion.timeline.doctorAssigned.message', {
        name: doctorName,
      }),
    },
    DOCTOR_CONFIRMED: {
      icon: CheckCircle2,
      color: 'text-green-500',
      title: t('secondOpinion.timeline.doctorConfirmed.title'),
      message: t('secondOpinion.timeline.doctorConfirmed.message'),
    },
    MORE_INFO_NEEDED: {
      icon: Info,
      color: 'text-orange-500',
      title: t('secondOpinion.timeline.moreInfoNeeded.title'),
      message: t('secondOpinion.timeline.moreInfoNeeded.message'),
    },
    INFO_SUBMITTED: {
      icon: FilePen,
      color: 'text-blue-500',
      title: t('secondOpinion.timeline.infoSubmitted.title'),
      message: t('secondOpinion.timeline.infoSubmitted.message'),
    },
  };
}

export default function SecondOpinion() {
  const params = useParams();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [isAttachmentsDialogOpen, setIsAttachmentsDialogOpen] = useState(false);

  const {
    data: secondOpinion,
    isLoading,
    refetch,
  } = useData('GET /second-opinions/{id}', { id: params.id! });

  const payment = usePaymentInfo({
    kind: 'second_opinion',
    urgent: secondOpinion?.urgent ?? false,
  });

  const moreInfoNeeded = secondOpinion?.timeline.find(
    (event) => event.type === 'MORE_INFO_NEEDED',
  );

  const lastTimelineEvent = secondOpinion?.timeline[0];
  const infoSubmittedAfterRequest =
    moreInfoNeeded && lastTimelineEvent?.type === 'INFO_SUBMITTED';

  const statusMap = useStatusMap({ request: secondOpinion });
  const historyEventMap = useHistoryEventDetails({ request: secondOpinion });

  if (isLoading) return <Loading />;

  if (!secondOpinion) {
    return <div>{t('secondOpinion.notFound')}</div>;
  }

  const created = secondOpinion.timeline.find((it) => it.type === 'CREATED')!;
  const createdContext = JSON.parse(created?.context || '{}') as {
    patient: GetCurrentUser;
  };
  const patientGender = createdContext.patient?.b2cProfile?.gender;
  const speciality = getLocalizedSpeciality(secondOpinion.speciality, language);

  const patientSummary = [
    {
      label: t('appointments.detail.patientSummary.name'),
      value: displayName(createdContext.patient) ?? '',
    },
    {
      show: createdContext.patient?.b2cProfile != null,
      label: t('appointments.detail.patientSummary.age'),
      value: `${
        createdContext.patient?.b2cProfile
          ? toAge(createdContext.patient.b2cProfile)
          : t('common.notSpecified')
      }`,
    },
    {
      show: createdContext.patient?.b2cProfile != null,
      label: t('appointments.detail.patientSummary.gender'),
      value:
        patientGender === 'male' || patientGender === 'female'
          ? t(`common.${patientGender}`)
          : '',
    },
    {
      label: t('appointments.detail.patientSummary.specialty'),
      value: speciality.name,
    },
    {
      label: t('secondOpinion.priority'),
      value: secondOpinion.urgent ? t('common.urgent') : t('common.normal'),
    },
    {
      label: t('appointments.detail.patientSummary.attachments'),
      value: `${
        secondOpinion.files.length > 0
          ? `+${secondOpinion.files.length}`
          : t('appointments.detail.patientSummary.noAttachments')
      }`,
    },
  ].filter((item) => item.show !== false);

  const isB2BPatient = !createdContext.patient?.b2cProfile;

  return (
    <>
      <Header
        title={
          <AppointmentHeader
            reservationNumber={secondOpinion.counter}
            status={secondOpinion.status}
            showBackButton={true}
            price={isB2BPatient ? undefined : payment.amountDue.shortLabel}
          />
        }
      >
        <div className="hidden md:block">
          <ProfileCard />
        </div>
      </Header>
      <div className="min-h-screen px-4 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="col-span-1 flex flex-col gap-6 lg:col-span-2">
            <SecondOpinionCard
              record={secondOpinion}
              statusInfoMap={statusMap}
              patientSummary={patientSummary}
              onViewAttachments={() => setIsAttachmentsDialogOpen(true)}
              totalAmount={
                isB2BPatient ? undefined : payment.amountDue.shortLabel
              }
              footer={
                <>
                  {moreInfoNeeded && !infoSubmittedAfterRequest && (
                    <SubmitInfoButton secondOpinion={secondOpinion} />
                  )}
                </>
              }
            >
              {moreInfoNeeded && (
                <div className="mt-7">
                  <AdditionalInfo
                    doctorNotes={
                      JSON.parse(moreInfoNeeded.context ?? '{}').requestedInfo
                    }
                  />
                </div>
              )}
            </SecondOpinionCard>

            <AppointmentTimeline
              timeline={secondOpinion.timeline}
              historyEventMap={historyEventMap}
            />
          </div>

          <div className="sticky top-4 col-span-1 flex flex-col gap-6 self-start">
            <PatientInfoCard user={createdContext.patient} showUsageLimit />
            {!isB2BPatient && (
              <PaymentDetailsCard
                finalAmount={payment.total.shortLabel}
                insuranceDiscount={payment.insuranceDiscount.shortLabel}
                totalAmount={payment.amountDue.shortLabel}
              />
            )}
          </div>
        </div>
      </div>

      <AttachmentsDialog
        open={isAttachmentsDialogOpen}
        onOpenChange={setIsAttachmentsDialogOpen}
        record={secondOpinion}
      />
    </>
  );
}
